import { type ReactNode, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from './form';

export function PageHeader({
  eyebrow,
  title,
  actions,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <header className="tbm-page-header">
      <div>
        {eyebrow ? <p className="muted" style={{ margin: 0, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 'var(--tbm-text-xs)' }}>{eyebrow}</p> : null}
        <h1>{title}</h1>
        {children}
      </div>
      {actions ? <div className="tbm-toolbar">{actions}</div> : null}
    </header>
  );
}

export function Breadcrumbs({ items }: { items: Array<{ to?: string; label: string }> }): JSX.Element {
  return (
    <nav className="tbm-crumbs">
      {items.map((item, i) => (
        <span key={item.label}>
          {i > 0 ? ' / ' : null}
          {item.to ? <NavLink to={item.to}>{item.label}</NavLink> : item.label}
        </span>
      ))}
    </nav>
  );
}

export function Toolbar({ children }: { children: ReactNode }): JSX.Element {
  return <div className="tbm-toolbar">{children}</div>;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`tbm-card ${className}`}>{children}</div>;
}

export function Section({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className="tbm-section">
      <div className="tbm-section__head">
        <h2 style={{ margin: 0, fontSize: 'var(--tbm-text-lg)' }}>{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Tabs({ items }: { items: Array<{ to: string; label: string; end?: boolean }> }): JSX.Element {
  return (
    <nav className="tbm-tabs">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `tbm-tab${isActive ? ' is-active' : ''}`}>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function DataTable<T>({
  rows,
  columns,
  empty = 'Aucun élément',
  leading,
}: {
  rows: T[];
  columns: Array<{ key: string; header: string; cell: (row: T) => ReactNode }>;
  empty?: ReactNode;
  leading?: ReactNode;
}): JSX.Element {
  if (!rows.length && !leading) return <div className="tbm-empty">{empty}</div>;
  return (
    <div className="tbm-table-wrap">
      <table className="tbm-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leading}
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col.key}>{col.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DescriptionList({ items }: { items: Array<{ label: string; value: ReactNode }> }): JSX.Element {
  return (
    <dl className="tbm-dl">
      {items.map((item) => (
        <div key={item.label} style={{ display: 'contents' }}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Dialog({
  open,
  title,
  children,
  onClose,
  onConfirm,
  confirmLabel = 'Confirmer',
  danger,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  onConfirm?: () => void;
  confirmLabel?: string;
  danger?: boolean;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <div className="tbm-overlay" onClick={onClose}>
      <div className="tbm-dialog" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        {children}
        <div className="tbm-toolbar" style={{ marginTop: 'var(--tbm-space-4)' }}>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          {onConfirm ? (
            <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function Drawer({ open, title, children, onClose }: { open: boolean; title: string; children: ReactNode; onClose: () => void }): JSX.Element | null {
  if (!open) return null;
  return (
    <aside className="tbm-drawer">
      <div className="tbm-toolbar">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <Button variant="ghost" onClick={onClose}>Fermer</Button>
      </div>
      {children}
    </aside>
  );
}

export function ToastHost({ message }: { message: string | null }): JSX.Element | null {
  const [visible, setVisible] = useState(message);
  useEffect(() => setVisible(message), [message]);
  if (!visible) return null;
  return (
    <div className="tbm-toast-host">
      <div className="tbm-toast">{visible}</div>
    </div>
  );
}

export function SplitPane({ side, children }: { side: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div className="tbm-split">
      <div>{side}</div>
      <div>{children}</div>
    </div>
  );
}
